// The only module that knows how a saved column layout maps onto the columns drawn (spec 010 §2.2).
// Pure: no React, no design-system imports - the same isolation opportunityFilters.ts keeps.
// Keyed by design-system column id (columnIdFor for native columns, customField_<defId> for custom ones),
// so a rename never invalidates a layout (FR2.4).
import {OPPORTUNITY_COLUMNS, columnIdFor} from './opportunityColumns';

export type ColumnLayout = {order: string[]; widths: Record<string, number>};

const layoutKey = (scope: string): string => `column-layout-${scope}-v1`;

const NATIVE_IDS: string[] = OPPORTUNITY_COLUMNS.map(c => columnIdFor(c.name));

export function readColumnLayout(scope: string): ColumnLayout | null {
	try {
		const raw = localStorage.getItem(layoutKey(scope));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		const ok =
			parsed &&
			Array.isArray(parsed.order) &&
			parsed.order.every((id: unknown) => typeof id === 'string') &&
			parsed.widths &&
			typeof parsed.widths === 'object' &&
			Object.values(parsed.widths).every(w => typeof w === 'number' && Number.isFinite(w));
		return ok ? {order: parsed.order, widths: parsed.widths} : null;
	} catch {
		return null;
	}
}

export function writeColumnLayout(scope: string, layout: ColumnLayout): void {
	try {
		localStorage.setItem(layoutKey(scope), JSON.stringify(layout));
	} catch {
		/* localStorage unavailable */
	}
}

/** Reset (D12): an absent layout IS the default, so deleting the key saves the reset. Never touches visibility. */
export function clearColumnLayout(scope: string): void {
	try {
		localStorage.removeItem(layoutKey(scope));
	} catch {
		/* localStorage unavailable */
	}
}

export const defaultColumnLayout = (customFieldIdsInToggleOrder: string[]): ColumnLayout => ({
	order: [...NATIVE_IDS, ...customFieldIdsInToggleOrder],
	widths: {},
});

/**
 * The layout to draw. `knownIds` is EVERY column that exists (native and custom, shown or not), so a hidden
 * column keeps its slot and width (D10, AC8). Unknown ids are dropped; columns the saved layout never placed
 * (a custom field turned on for the first time, a native column added after it was saved) go to the right
 * end - custom fields in the order they were turned on.
 */
export function resolveColumnLayout(
	saved: ColumnLayout | null,
	knownIds: string[],
	customFieldIdsInToggleOrder: string[]
): ColumnLayout {
	if (!saved) return defaultColumnLayout(customFieldIdsInToggleOrder);
	const known = new Set(knownIds);
	const order = saved.order.filter(id => known.has(id));
	const placed = new Set(order);
	const unplacedNative = NATIVE_IDS.filter(id => known.has(id) && !placed.has(id));
	const unplacedCustom = customFieldIdsInToggleOrder.filter(id => known.has(id) && !placed.has(id));
	const widths = Object.fromEntries(Object.entries(saved.widths).filter(([id]) => known.has(id)));
	return {order: [...order, ...unplacedNative, ...unplacedCustom], widths};
}

/**
 * The order to save after a reorder. The Table reports only the columns it renders, so a custom field that is
 * switched off is missing from `next`; saving `next` as-is would lose that field's slot (D10, AC8). Ids missing from
 * `next` keep their index in `saved`, and the reported ids fill the other slots in their new order.
 */
export function mergeColumnOrder(saved: string[], next: string[]): string[] {
	const reported = new Set(next);
	let i = 0;
	const merged = saved.map(id => (reported.has(id) ? next[i++] : id));
	return [...merged, ...next.slice(i)];
}

/**
 * Whether the columns drawn are laid out as the default would draw them - drives the Reset button's visibility
 * (AC9, AC11). Only `shownIds` are compared: a hidden column keeps its saved slot and width (D10), but the user
 * can't see them, so they alone must not offer a Reset that changes nothing on screen. Ignores visibility (D22).
 */
export function isDefaultColumnLayout(
	resolved: ColumnLayout,
	customFieldIdsInToggleOrder: string[],
	shownIds: ReadonlySet<string>
): boolean {
	const order = resolved.order.filter(id => shownIds.has(id));
	const def = defaultColumnLayout(customFieldIdsInToggleOrder).order.filter(id => shownIds.has(id));
	return (
		Object.keys(resolved.widths).every(id => !shownIds.has(id)) &&
		order.length === def.length &&
		order.every((id, i) => id === def[i])
	);
}
